import { useState, useEffect } from 'react';
import { useRouteError, isRouteErrorResponse } from 'react-router-dom';

// A tab left open while a new build deploys still points at the old chunk
// names; its next lazy page or component fails with "Loading chunk N failed"
// (ChunkLoadError). One automatic reload picks up the new build. A
// sessionStorage time stamp allows at most one reload per 10 s, so a chunk
// that keeps failing shows the error page instead of reloading in a loop.

export const CHUNK_RELOAD_KEY = 'clix:chunk-reload-at';
export const CHUNK_RELOAD_WINDOW_MS = 10_000;

const CHUNK_MESSAGES = [
  /Loading (CSS )?chunk [\w-]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
];

export const isChunkLoadError = (error: unknown) => {
  if (!(error instanceof Error)) {
    return false;
  }
  return (
    error.name === 'ChunkLoadError' ||
    CHUNK_MESSAGES.some((pattern) => pattern.test(error.message))
  );
};

/** Starts one reload unless one ran in the last 10 s. True when it did. */
export const reloadOnceForChunkError = (now = Date.now()) => {
  try {
    const last = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY));
    const since = now - last;
    if (last && since >= 0 && since < CHUNK_RELOAD_WINDOW_MS) {
      return false;
    }
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
  } catch {
    // No storage, no guard: never reload unguarded.
    return false;
  }
  window.location.reload();
  return true;
};

/** React Router's own error page, as it shows in a production build. */
const DefaultRouteError = ({ error }: { error: unknown }) => {
  let message: string;
  if (isRouteErrorResponse(error)) {
    message = `${error.status} ${error.statusText}`;
  } else if (error instanceof Error) {
    message = error.message;
  } else {
    message = JSON.stringify(error);
  }
  const stack = error instanceof Error ? error.stack : null;
  return (
    <>
      <h2>Unexpected Application Error!</h2>
      <h3 style={{ fontStyle: 'italic' }}>{message}</h3>
      {stack ? (
        <pre
          style={{
            padding: '0.5rem',
            backgroundColor: 'rgba(200,200,200, 0.5)',
          }}
        >
          {stack}
        </pre>
      ) : null}
    </>
  );
};

/** errorElement of the top-level routes: every lazy page and component
 * renders under them, so a failed chunk anywhere lands here. */
export const RouteError = () => {
  const error = useRouteError();
  const isChunk = isChunkLoadError(error);
  const [reloading, setReloading] = useState(isChunk);

  useEffect(() => {
    if (isChunk && !reloadOnceForChunkError()) {
      setReloading(false);
    }
  }, [isChunk]);

  return reloading ? null : <DefaultRouteError error={error} />;
};
